// Where the edge ribbon sits relative to a vertical scrollbar.
//
// A classic scrollbar consumes layout space. `position: fixed; right: 0` is
// already the inner edge of that gutter, so the ribbon sits against it.
//
// An overlay scrollbar does not. Firefox on Windows (and Linux), including
// with a theme that switches the browser to its non-native scrollbar, paints
// the bar on top of the viewport edge and gives it the hit target. The layout
// gutter measures 0, so `right: 0` slides the ribbon under the bar. The full
// tab is wide enough to remain partly visible. The 5px hairline is not, and
// the scrollbar takes the pointer, so the reporter cannot peek or restore it.
//
// macOS and iOS overlay bars auto-hide and do not pin that edge; those stay
// flush with the window.

/** Wide enough to clear a themed overlay track (about 12–17px) with a little room. */
export const OVERLAY_SCROLLBAR_CLEARANCE = 20;

export interface LauncherEdgeMetrics {
  innerWidth: number;
  clientWidth: number;
  scrollHeight: number;
  clientHeight: number;
  /** Windows and Linux overlay tracks cover the edge. Apple's do not. */
  overlayCoversEdge: boolean;
  rtl: boolean;
}

/**
 * Pixels to set as the launcher's `right` offset.
 * `0` means the stylesheet's flush-right position, which already clears a
 * classic scrollbar.
 */
export function launcherRightInset(metrics: LauncherEdgeMetrics): number {
  if (metrics.rtl) return 0;
  const gutter = metrics.innerWidth - metrics.clientWidth;
  // Classic scrollbar. Subpixel rounding can report a fraction of a pixel
  // when no bar is there; a real gutter is several pixels.
  if (gutter > 1) return 0;
  const overflows = metrics.scrollHeight > metrics.clientHeight + 1;
  if (!overflows || !metrics.overlayCoversEdge) return 0;
  return OVERLAY_SCROLLBAR_CLEARANCE;
}

export function overlayScrollbarCoversEdge(platform: string, userAgent: string): boolean {
  return !/Mac|iPhone|iPad|iPod/i.test(`${platform}\n${userAgent}`);
}
